import {
  ReactElement,
  RefObject,
  UIEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'

import {
  AnimatedCopyButton,
  ChevronRightIcon,
  RefreshIcon,
  RenderIf,
} from 'components'
import {selectUser} from 'features/User'
import {AnalyticsRoutesEnum, DopamineHistoryMessage} from 'interfaces'
import {DopamineAssistantComposer} from './DopamineAssistantComposer'
import {DopamineAssistantProgress} from './DopamineAssistantProgress'
import {DopamineAssistantResult} from './DopamineAssistantResult'
import {DopamineAssistantMark} from './DopamineAssistantMark'
import {DopamineAssistantSuggestionIcon} from './DopamineAssistantSuggestionIcon'
import {
  DopamineAssistantSessionController,
  DopaminePromptSubmission,
  DopaminePromptSuggestion,
  DopamineRunProgress,
} from './DopamineAssistant.types'
import {useDopamineAssistantRun} from './hooks'

const VIEW_CONFIG = {
  overview: {
    contextKey: 'ANALYTICS_OVERVIEW_CONTEXT',
    introKey: 'DOPAMINE_ANALYTICS_INTRO',
    suggestions: [
      {key: 'TOP_AI_APPS', icon: 'apps'},
      {key: 'SENSITIVE_DATA_TO_AI', icon: 'shield'},
      {key: 'USERS_TO_INVESTIGATE', icon: 'users'},
    ],
  },
  policy: {
    contextKey: 'ANALYTICS_POLICY_CONTEXT',
    introKey: 'DOPAMINE_POLICY_INTRO',
    suggestions: [
      {key: 'MOST_TRIGGERED_POLICY_RULES', icon: 'shield'},
      {key: 'USERS_BYPASSING_AI_POLICY', icon: 'users'},
      {key: 'BLOCKS_BY_APP', icon: 'apps'},
    ],
  },
  shadowIT: {
    contextKey: 'ANALYTICS_SHADOW_IT_CONTEXT',
    introKey: 'DOPAMINE_SHADOW_IT_INTRO',
    suggestions: [
      {key: 'NEW_AI_APPS', icon: 'apps'},
      {key: 'RISKY_UNSANCTIONED_APPS', icon: 'shield'},
      {key: 'AI_AGENTS_AND_MCP', icon: 'apps'},
    ],
  },
} as const

const GENERIC_ANALYTICS_VIEW_CONFIG = {
  ...VIEW_CONFIG.overview,
  contextKey: 'ANALYTICS_CONTEXT',
} as const

const SCROLL_FOLLOW_THRESHOLD_PX = 32

const normalizePathname = (pathname: string): string => {
  if (pathname.length === 0) return '/'

  let lastPathCharacterIndex = pathname.length - 1

  while (
    lastPathCharacterIndex > 0 &&
    pathname[lastPathCharacterIndex] === '/'
  ) {
    lastPathCharacterIndex -= 1
  }

  return pathname.slice(0, lastPathCharacterIndex + 1).toLowerCase()
}

const getViewConfig = (pathname: string) => {
  const normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === AnalyticsRoutesEnum.ANALYTICS_OVERVIEW) {
    return VIEW_CONFIG.overview
  }
  if (normalizedPathname === AnalyticsRoutesEnum.ANALYTICS_POLICY) {
    return VIEW_CONFIG.policy
  }
  if (normalizedPathname === AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT) {
    return VIEW_CONFIG.shadowIT
  }
  return GENERIC_ANALYTICS_VIEW_CONFIG
}

const getGreetingKey = (): string => {
  const hour = new Date().getHours()
  if (hour < 12) return 'GOOD_MORNING'
  if (hour < 18) return 'GOOD_AFTERNOON'
  return 'GOOD_EVENING'
}

type ConversationView = 'loading' | 'conversation' | 'empty' | 'error'

type DopamineHistorySegment =
  | {id: string; message: DopamineHistoryMessage; type: 'user'}
  | {id: string; messages: DopamineHistoryMessage[]; type: 'response'}

const groupHistoryMessages = (
  messages: DopamineHistoryMessage[],
): DopamineHistorySegment[] =>
  messages.reduce<DopamineHistorySegment[]>((segments, message) => {
    if (message.role === 'user') {
      segments.push({id: message.id, message, type: 'user'})
      return segments
    }

    const currentSegment = segments.at(-1)
    if (currentSegment?.type === 'response') {
      currentSegment.messages.push(message)
      return segments
    }

    segments.push({id: message.id, messages: [message], type: 'response'})
    return segments
  }, [])

const createCompletedHistoryProgress = (
  messages: DopamineHistoryMessage[],
): DopamineRunProgress => ({
  endedAt: null,
  failureMessage: null,
  items: messages.map(message => ({
    activeLabel: message.content,
    completedLabel: message.content,
    id: message.id,
    kind: 'tool',
    status: 'completed',
  })),
  runId: `history:${messages.map(({id}) => id).join(':')}`,
  startedAt: null,
  status: 'completed',
})

const getHistoryCopyValue = (messages: DopamineHistoryMessage[]): string =>
  messages.reduce(
    (copyValue, message) =>
      message.role === 'assistant' && message.content.trim()
        ? message.content
        : copyValue,
    '',
  )

const getConversationView = (
  isHistoryLoading: boolean,
  hasVisibleConversation: boolean,
  isHistoryError: boolean,
): ConversationView => {
  if (isHistoryLoading) return 'loading'
  if (isHistoryError) return 'error'
  if (hasVisibleConversation) return 'conversation'
  return 'empty'
}

const AssistantMessageHeader = ({
  isRunning = false,
}: {
  isRunning?: boolean
}): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})

  return (
    <div className="dopamine-ai-message-header flex items-center gap-2 pl-px">
      <span className="dopamine-ai-message-avatar flex h-[1.4375rem] w-[1.4375rem] shrink-0 items-center justify-center rounded-[0.4375rem] text-white">
        <DopamineAssistantMark
          animated={isRunning}
          className="h-[0.9375rem] w-[0.9375rem]"
        />
      </span>
      <span className="abc-bold text-10px uppercase tracking-[0.14em] text-dopamine-muted">
        {t('DOPAMINE')}
      </span>
    </div>
  )
}

type DopamineAssistantConversationProps = {
  composerRef: RefObject<HTMLTextAreaElement>
  isVisible?: boolean
  onStartNewChat(): void
  session: DopamineAssistantSessionController
}

export const DopamineAssistantConversation = ({
  composerRef,
  isVisible = true,
  onStartNewChat,
  session,
}: DopamineAssistantConversationProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const {name} = useSelector(selectUser)
  const {pathname} = useLocation()
  const normalizedPathname = normalizePathname(pathname)
  const [draft, setDraft] = useState('')
  const conversationRef = useRef<HTMLElement>(null)
  const retrySubmissionLockRef = useRef(false)
  const shouldFollowConversationRef = useRef(true)
  const settledHistoryRef = useRef(session.history)
  const {
    isRunning,
    queuedTurn,
    removeQueuedTurn,
    submitPrompt: queuePrompt,
    turns,
  } = useDopamineAssistantRun({
    clientSessionId: session.clientSessionId,
    conversationRevision: session.conversationRevision,
    onSessionExpired: session.markSessionReadOnly,
    onSessionSettled: session.refreshSessions,
    onSessionStarted: session.markSessionStarted,
  })
  const displayName = name.trim().split(/\s+/)[0] || t('THERE')
  const viewConfig = getViewConfig(pathname)
  const suggestions: DopaminePromptSuggestion[] = viewConfig.suggestions.map(
    ({key, icon}) => ({
      displayText: t(`${key}_TITLE`),
      description: t(`${key}_DESCRIPTION`),
      id: key,
      query: t(`${key}_QUERY`),
      icon,
    }),
  )

  useLayoutEffect(() => {
    shouldFollowConversationRef.current = true
    setDraft('')
  }, [session.clientSessionId, session.conversationRevision])

  useLayoutEffect(() => {
    const conversation = conversationRef.current
    if (!conversation || !isVisible || !shouldFollowConversationRef.current)
      return

    conversation.scrollTop = Math.max(
      0,
      conversation.scrollHeight - conversation.clientHeight,
    )
  }, [
    isVisible,
    normalizedPathname,
    session.clientSessionId,
    session.conversationRevision,
    session.history.length,
    turns,
  ])

  const handleConversationScroll = (event: UIEvent<HTMLElement>): void => {
    const {clientHeight, scrollHeight, scrollTop} = event.currentTarget
    shouldFollowConversationRef.current =
      scrollHeight - scrollTop - clientHeight <= SCROLL_FOLLOW_THRESHOLD_PX
  }

  const submitPrompt = (submission: DopaminePromptSubmission): void => {
    if (
      session.isReadOnly ||
      session.isHistoryError ||
      session.isHistoryRefreshing
    ) {
      return
    }

    shouldFollowConversationRef.current = true
    queuePrompt(submission)
  }

  const hasPendingTurn = turns.some(
    turn =>
      turn.status === 'active' ||
      turn.status === 'queued' ||
      turn.status === 'submitting',
  )
  const visibleHistory = hasPendingTurn
    ? settledHistoryRef.current
    : session.history
  const historyMessageIds = new Set(visibleHistory.map(message => message.id))
  const visibleTurns = turns.filter(
    turn =>
      turn.status !== 'completed' ||
      !turn.answer.messageId ||
      !historyMessageIds.has(turn.answer.messageId),
  )
  const historySegments = groupHistoryMessages(visibleHistory)
  const hasVisibleConversation =
    historySegments.length > 0 || visibleTurns.length > 0
  const isVisibleHistoryError = session.isHistoryError && !hasPendingTurn
  const isRetryDisabled =
    hasPendingTurn || isVisibleHistoryError || session.isHistoryRefreshing

  useLayoutEffect(() => {
    if (!hasPendingTurn) settledHistoryRef.current = session.history
  }, [hasPendingTurn, session.history])

  useEffect(() => {
    if (!isRunning) retrySubmissionLockRef.current = false
  }, [isRunning])

  const retryPrompt = (submission: DopaminePromptSubmission): void => {
    if (retrySubmissionLockRef.current || isRetryDisabled) return

    retrySubmissionLockRef.current = true
    submitPrompt(submission)
  }

  const conversationView = getConversationView(
    session.isHistoryLoading,
    hasVisibleConversation,
    isVisibleHistoryError,
  )

  const submitSuggestion = (suggestion: DopaminePromptSuggestion): void => {
    setDraft('')
    submitPrompt(suggestion)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden">
        <RenderIf isTrue={conversationView === 'loading'}>
          <div className="flex h-full items-center justify-center px-4">
            <output className="text-center text-xs text-gray-500">
              {t('DOPAMINE_LOADING_CHAT')}
            </output>
          </div>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'conversation'}>
          <section
            aria-label={t('DOPAMINE_CONVERSATION')}
            className="flex h-full flex-col gap-1 overflow-y-auto px-3 py-4"
            ref={conversationRef}
            onScroll={handleConversationScroll}
          >
            {historySegments.map(segment => {
              if (segment.type === 'user') {
                return (
                  <p
                    className="dopamine-user-message max-w-dopamine-message self-end whitespace-pre-wrap break-words text-sm leading-5.5 text-white"
                    key={segment.id}
                  >
                    {segment.message.content}
                  </p>
                )
              }

              const copyValue = getHistoryCopyValue(segment.messages)
              const progressMessages = segment.messages.filter(
                message => message.role === 'tool_call' && message.content,
              )
              const firstProgressMessageId = progressMessages[0]?.id

              return (
                <div
                  className="mb-3 mt-3 flex w-full flex-col items-stretch gap-2"
                  key={segment.id}
                >
                  <AssistantMessageHeader />
                  <div className="dopamine-message-surface dopamine-ai-message-surface rounded-[0.875rem] rounded-tl-[0.3125rem] px-[0.9375rem] py-3.5 text-[#f4f4f6]">
                    {segment.messages.map((message, index) => {
                      if (message.role === 'assistant') {
                        return (
                          <div
                            className={index === 0 ? undefined : 'mt-4'}
                            key={message.id}
                          >
                            <DopamineAssistantResult
                              answer={{
                                markdown: message.content,
                                messageId: message.id,
                                runId: session.clientSessionId,
                                status: 'completed',
                              }}
                              embedded
                              showActions={false}
                            />
                          </div>
                        )
                      }

                      if (
                        message.role === 'tool_call' &&
                        message.id === firstProgressMessageId
                      ) {
                        return (
                          <div className="mt-4" key={message.id}>
                            <DopamineAssistantProgress
                              progress={createCompletedHistoryProgress(
                                progressMessages,
                              )}
                            />
                          </div>
                        )
                      }

                      return null
                    })}
                    {copyValue && (
                      <div className="mt-3 flex items-center gap-1.5 border-t-0.5 border-white/5 pt-2.5">
                        <AnimatedCopyButton
                          ariaLabel={t('DOPAMINE_COPY_ANSWER')}
                          className="dopamine-answer-copy m-0 h-4 w-4"
                          dataCy={`dopamine-assistant-copy-${segment.id}`}
                          size="md"
                          value={copyValue}
                          variant="default"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            {visibleTurns.map(turn =>
              turn.status === 'queued' ? null : (
                <div className="flex flex-col" key={turn.id}>
                  <p className="dopamine-user-message max-w-dopamine-message self-end whitespace-pre-wrap break-words text-sm leading-5.5 text-white">
                    {turn.submission.displayText}
                  </p>
                  <div className="mb-3 mt-3 flex w-full flex-col items-stretch gap-2">
                    <AssistantMessageHeader
                      isRunning={
                        turn.status === 'active' || turn.status === 'submitting'
                      }
                    />
                    <div className="dopamine-message-surface dopamine-ai-message-surface rounded-[0.875rem] rounded-tl-[0.3125rem] px-[0.9375rem] py-3.5 text-[#f4f4f6]">
                      {turn.status === 'submitting' ? (
                        <output className="text-11px text-dopamine-muted">
                          {t('INVESTIGATING')}
                        </output>
                      ) : (
                        <DopamineAssistantProgress progress={turn.progress} />
                      )}
                      <DopamineAssistantResult
                        answer={turn.answer}
                        embedded
                        isRetryDisabled={isRetryDisabled}
                        onRetry={
                          turn.status === 'completed' && !session.isReadOnly
                            ? () => retryPrompt(turn.submission)
                            : undefined
                        }
                      />
                      {turn.status === 'failed' && !session.isReadOnly ? (
                        <button
                          aria-label={t('DOPAMINE_RETRY_ANSWER')}
                          className="dopamine-answer-action mt-3 focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent disabled:opacity-50"
                          disabled={isRetryDisabled}
                          title={t('DOPAMINE_RETRY_ANSWER')}
                          type="button"
                          onClick={() => retryPrompt(turn.submission)}
                        >
                          <RefreshIcon
                            classes="h-3.5 w-3.5"
                            height={14}
                            strokeColor="currentColor"
                            width={14}
                          />
                          <span>{t('RETRY')}</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ),
            )}
          </section>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'error'}>
          <section
            className="flex h-full flex-col items-center justify-center px-4 text-center"
            role="alert"
          >
            <p className="text-xs text-red-400">
              {t('DOPAMINE_CHAT_LOAD_ERROR')}
            </p>
            <button
              className="mt-3 text-xs font-semibold text-dopamine-accent hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
              type="button"
              onClick={onStartNewChat}
            >
              {t('DOPAMINE_NEW_CHAT')}
            </button>
          </section>
        </RenderIf>
        <RenderIf isTrue={conversationView === 'empty'}>
          <div className="flex h-full flex-col">
            <section className="shrink-0 px-4 pb-4 pt-5">
              <p className="mb-3 text-11px font-bold uppercase tracking-[0.15em] text-dopamine-muted">
                {t(viewConfig.contextKey)}
              </p>
              <h3 className="abc-light text-2xl font-normal leading-[1.15] text-white">
                {t(getGreetingKey())},{' '}
                <span className="text-dopamine-accent">{displayName}</span>
              </h3>
              <RenderIf isTrue={!session.isReadOnly}>
                <p className="mt-2.5 max-w-xs text-sm leading-5.5 text-dopamine-muted">
                  {t(viewConfig.introKey)}
                </p>
              </RenderIf>
            </section>
            <RenderIf isTrue={!session.isReadOnly}>
              <section
                aria-labelledby="dopamine-assistant-suggestions"
                className="min-h-0 flex-1 overflow-y-auto px-4 pb-3"
              >
                <h4
                  className="mb-2 px-1 text-10px uppercase text-gray-600"
                  id="dopamine-assistant-suggestions"
                >
                  {t('SUGGESTED_FOR_THIS_VIEW')}
                </h4>
                {suggestions.map(suggestion => (
                  <button
                    className="group/suggestion relative flex w-full items-center gap-3 border-b-0.5 border-white/5 bg-transparent px-2 py-3 text-left transition-colors duration-base last:border-0 hover:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-dopamine-accent disabled:cursor-not-allowed disabled:opacity-50"
                    data-cy={`dopamine-assistant-suggestion-${suggestion.id.toLowerCase()}`}
                    disabled={session.isHistoryRefreshing}
                    key={suggestion.id}
                    type="button"
                    onClick={() => submitSuggestion(suggestion)}
                  >
                    <span className="absolute left-0 top-1/2 h-0 w-0.5 -translate-y-1/2 rounded-full bg-dopamine-accent transition-all duration-base group-hover/suggestion:h-6 group-focus-visible/suggestion:h-6" />
                    <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center text-dopamine-accent transition-transform duration-base group-hover/suggestion:scale-110 motion-reduce:transition-none">
                      <DopamineAssistantSuggestionIcon icon={suggestion.icon} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-white transition-transform duration-base group-hover/suggestion:translate-x-0.5 motion-reduce:transition-none">
                        {suggestion.displayText}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-dopamine-muted">
                        {suggestion.description}
                      </span>
                    </span>
                    <ChevronRightIcon
                      classes="text-gray-500 transition-transform duration-base group-hover/suggestion:translate-x-0.5"
                      height={12}
                      strokeColor="currentColor"
                      strokeWidth={1.5}
                      width={7}
                    />
                  </button>
                ))}
              </section>
            </RenderIf>
          </div>
        </RenderIf>
      </div>
      {session.isHistoryRefreshing && !session.isHistoryLoading ? (
        <p aria-live="polite" className="sr-only">
          {t('DOPAMINE_LOADING_CHAT')}
        </p>
      ) : null}
      {isVisibleHistoryError && conversationView !== 'error' ? (
        <p
          className="shrink-0 border-t-0.5 border-white/10 px-4 py-4 text-center text-xs text-red-400"
          role="alert"
        >
          {t('DOPAMINE_CHAT_LOAD_ERROR')}
        </p>
      ) : null}
      {session.isReadOnly ? (
        <fieldset
          aria-labelledby="dopamine-assistant-session-ended"
          className="shrink-0 border-t-0.5 border-white/10 px-4 py-3 text-center"
        >
          <p
            className="text-xs leading-5 text-gray-500"
            id="dopamine-assistant-session-ended"
          >
            {t('DOPAMINE_SESSION_ENDED')}
          </p>
          <button
            className="mt-2 text-xs font-semibold text-dopamine-accent hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-dopamine-accent"
            type="button"
            onClick={onStartNewChat}
          >
            {t('DOPAMINE_NEW_CHAT')}
          </button>
        </fieldset>
      ) : null}
      {!session.isReadOnly && !session.isHistoryLoading ? (
        <DopamineAssistantComposer
          composerRef={composerRef}
          disabled={session.isHistoryError || session.isHistoryRefreshing}
          draft={draft}
          isRunning={isRunning}
          key={session.conversationRevision}
          queuedTurn={queuedTurn}
          onDraftChange={setDraft}
          onRemoveQueued={removeQueuedTurn}
          onSubmit={submitPrompt}
        />
      ) : null}
    </div>
  )
}
